import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ActionButton } from './ActionButton';

interface EditableLinkFieldProps {
  value: string | null | undefined;
  onSave: (value: string | null) => Promise<void>;
  addButtonLabel: string;
  linkButtonLabel: string;
  placeholder: string;
}

export function EditableLinkField({
  value,
  onSave,
  addButtonLabel,
  linkButtonLabel,
  placeholder,
}: EditableLinkFieldProps) {
  const [showInput, setShowInput] = useState(false);
  const [url, setUrl] = useState('');

  const ensureHttpsPrefix = (url: string): string => {
    if (!url) return url;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return `https://${url}`;
  };

  const handleSave = async () => {
    if (url.trim()) {
      const formattedUrl = ensureHttpsPrefix(url.trim());
      await onSave(formattedUrl);
      setShowInput(false);
      setUrl('');
    }
  };

  if (value && !showInput) {
    return (
      <div className="flex gap-2 items-center">
        <div className="flex-1">
          <ActionButton href={value} label={linkButtonLabel} />
        </div>
        <Button
          onClick={() => {
            setUrl(value);
            setShowInput(true);
          }}
          variant="ghost"
          size="sm"
          aria-label="Edit link"
          className="h-9 shrink-0 px-3 sm:px-2"
        >
          ✏️
        </Button>
      </div>
    );
  }

  if (showInput) {
    return (
      <div className="space-y-2">
        <div className="flex gap-2 items-center">
          <Input
            type="text"
            placeholder={placeholder}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="flex-1"
          />
          {value && (
            <Button
              onClick={async () => {
                await onSave(null);
                setShowInput(false);
                setUrl('');
              }}
              variant="ghost"
              size="sm"
              aria-label="Remove link"
              className="h-9 shrink-0 px-3 text-destructive sm:px-2"
            >
              🗑️
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          <Button onClick={handleSave} size="sm" className="flex-1">
            Save
          </Button>
          <Button
            onClick={() => {
              setShowInput(false);
              setUrl('');
            }}
            variant="outline"
            size="sm"
            className="flex-1"
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Button
      onClick={() => setShowInput(true)}
      variant="outline"
      className="w-full"
    >
      {addButtonLabel}
    </Button>
  );
}
